import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useCrm } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarioSync } from "@/components/CalendarioSync";
import { UserCog, Mail, KeyRound, Save, ShieldCheck, FileText, Download, Trash2, AlertTriangle } from "lucide-react";

const DOCS = [
  { file: "1-guida-titolare.pdf", titolo: "Guida Titolare", desc: "Tutte le sezioni e le operazioni tipiche", solo: "titolare" },
  { file: "2-guida-collaboratori.pdf", titolo: "Guida Collaboratori", desc: "Riassunto operativo da condividere col team" },
  { file: "3-compensi-obiettivi-bonus.pdf", titolo: "Compensi, Obiettivi e Bonus", desc: "Trattamento e dove modificarlo", solo: "titolare" },
  { file: "4-pubblicazione-app.pdf", titolo: "Come pubblicare l'app", desc: "Guida al deployment", solo: "titolare" },
  { file: "5-preventivo-costi.pdf", titolo: "Preventivo Costi", desc: "Stima costi hosting", solo: "titolare" },
];

export default function Account() {
  const { user, isTitolare, applyAuth } = useAuth();
  const { api, setSettings } = useCrm();
  const [email, setEmail] = useState(user?.email || "");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [pin, setPin] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const resetData = async () => {
    if (confirmText.trim().toUpperCase() !== "SVUOTA") { toast.error('Scrivi SVUOTA per confermare'); return; }
    setResetting(true);
    try {
      await api.resetData();
      toast.success("Tutti i dati sono stati svuotati. Ora puoi inserire i tuoi dati reali.");
      setResetOpen(false);
      setConfirmText("");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante lo svuotamento");
    } finally {
      setResetting(false);
    }
  };

  const save = async () => {
    if (!curPw) { toast.error("Inserisci la password attuale per confermare"); return; }
    if (newPw && newPw !== confirmPw) { toast.error("Le due nuove password non coincidono"); return; }
    const emailChanged = email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
    if (!emailChanged && !newPw) { toast.error("Nessuna modifica da salvare"); return; }
    setSaving(true);
    try {
      const res = await api.updateCredentials({
        current_password: curPw,
        new_email: emailChanged ? email.trim() : undefined,
        new_password: newPw || undefined,
      });
      applyAuth(res);
      setCurPw(""); setNewPw(""); setConfirmPw("");
      toast.success("Credenziali aggiornate correttamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const savePin = async () => {
    if (!pin.trim()) return;
    const s = await api.updateSettings({ controllo_pin: pin.trim() });
    setSettings(s);
    setPin("");
    toast.success("PIN Controllo aggiornato");
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
          <UserCog className="h-7 w-7 text-primary" /> Account
        </h1>
        <p className="text-slate-500 mt-1">Collega il calendario e gestisci le tue credenziali di accesso.</p>
      </div>

      <CalendarioSync />

      <Card className="p-6 animate-fade-up" data-testid="account-credentials-card">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Credenziali di accesso</h2>
          <Badge variant="outline" className="ml-auto capitalize">{user?.role}</Badge>
        </div>
        <p className="text-xs text-slate-500 mb-5">Cambia l'email (ID di accesso) e/o la password. Per sicurezza serve sempre la password attuale.</p>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>Email di accesso (ID)</Label>
            <Input data-testid="account-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@dominio.it" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Nuova password</Label>
              <Input data-testid="account-new-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Lascia vuoto per non cambiarla" />
            </div>
            <div className="grid gap-1.5">
              <Label>Conferma nuova password</Label>
              <Input data-testid="account-confirm-password-input" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
            <Label className="text-primary">Password attuale *</Label>
            <Input data-testid="account-current-password-input" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="Richiesta per confermare" className="max-w-sm" />
          </div>
          <div>
            <Button data-testid="account-save-button" onClick={save} disabled={saving} className="rounded-full gap-1.5">
              <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva modifiche"}
            </Button>
          </div>
        </div>
      </Card>

      {isTitolare && (
        <Card className="p-6 animate-fade-up" data-testid="account-pin-card">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">PIN sezione Controllo</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Codice per accedere al Controllo economico (protezione leggera, non è la password dell'account).</p>
          <div className="flex items-end gap-3 max-w-sm">
            <div className="grid gap-1.5 flex-1">
              <Label>Nuovo PIN</Label>
              <Input data-testid="account-new-pin-input" type="text" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="es. 4821" />
            </div>
            <Button data-testid="account-save-pin-button" onClick={savePin} variant="outline" className="rounded-full">Aggiorna PIN</Button>
          </div>
        </Card>
      )}

      <Card className="p-6 animate-fade-up" data-testid="account-docs-card">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Guide e Documenti</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Scarica le guide in PDF (con logo), pronte da leggere o inviare.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {DOCS.filter((d) => isTitolare || d.solo !== "titolare").map((d) => (
            <a
              key={d.file}
              href={`/docs/${d.file}`}
              download
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`doc-${d.file}`}
              className="group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3.5 hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-all"
            >
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">{d.titolo}</div>
                <div className="text-[11px] text-slate-400 truncate">{d.desc}</div>
              </div>
              <Download className="h-4 w-4 text-slate-300 group-hover:text-primary transition-colors shrink-0" />
            </a>
          ))}
        </div>
      </Card>

      <Card className="p-5 bg-slate-50 dark:bg-slate-900/40 border-dashed animate-fade-up">
        <div className="flex items-start gap-2.5 text-sm text-slate-500">
          <ShieldCheck className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
          <span>Le nuove credenziali vengono salvate in modo permanente e restano valide anche dopo eventuali riavvii dell'app. Dopo il cambio email userai la nuova email al prossimo accesso.</span>
        </div>
      </Card>

      {isTitolare && (
        <Card className="p-6 border-rose-200 dark:border-rose-500/30 animate-fade-up" data-testid="account-danger-card">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-5 w-5 text-rose-500" />
            <h2 className="text-lg font-semibold text-rose-600 dark:text-rose-400">Svuota tutti i dati</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4 max-w-xl">
            Elimina definitivamente <b>tutti gli SSD, il portfolio, le attività, i movimenti economici, le liste contatti e i collaboratori</b> (dati demo inclusi).
            Restano intatti il tuo <b>account Titolare</b> e le <b>impostazioni</b> (obiettivi, bonus, date di liquidazione, PIN).
            Usalo una sola volta dopo la pubblicazione per partire da un'app pulita. <b>L'operazione non è reversibile.</b>
          </p>
          <Button data-testid="open-reset-button" variant="outline" className="rounded-full gap-1.5 border-rose-300 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10" onClick={() => setResetOpen(true)}>
            <Trash2 className="h-4 w-4" /> Svuota tutti i dati
          </Button>
        </Card>
      )}

      <Dialog open={resetOpen} onOpenChange={(o) => !o && setResetOpen(false)}>
        <DialogContent className="max-w-md" data-testid="reset-data-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600"><AlertTriangle className="h-5 w-5" /> Conferma svuotamento</DialogTitle>
            <DialogDescription>
              Stai per eliminare in modo permanente tutti i dati operativi e i collaboratori. Account Titolare e impostazioni restano. L'operazione non è reversibile.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5 pt-1">
            <Label>Per confermare scrivi <b>SVUOTA</b></Label>
            <Input data-testid="reset-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="SVUOTA" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => setResetOpen(false)}>Annulla</Button>
            <Button data-testid="confirm-reset-button" className="rounded-full gap-1.5 bg-rose-600 hover:bg-rose-700" onClick={resetData} disabled={resetting || confirmText.trim().toUpperCase() !== "SVUOTA"}>
              <Trash2 className="h-4 w-4" /> {resetting ? "Svuotamento…" : "Svuota definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
